import {Component, computed, CUSTOM_ELEMENTS_SCHEMA, effect, inject, signal, viewChild} from '@angular/core';
import {IntersectionObserverDirective} from '../../directives/intersection-observer.directive';
import {WjFlexGrid, WjGridModule} from '@mescius/wijmo.angular2.grid';
import {ColumnGroupDeclaration, Wijmo, WjGridUtils} from '../../lib/wjgrid-utils';
import {
    DashboardTargetsMainComponent,
} from '../dashboard-targets-main/dashboard-targets-main.component';
import {sumBy} from 'es-toolkit';
import {WjChartModule} from '@mescius/wijmo.angular2.chart';
import {WjNavModule} from '@mescius/wijmo.angular2.nav';
import {WjChartInteractionModule} from '@mescius/wijmo.angular2.chart.interaction';
import {WjChartAnnotationModule} from '@mescius/wijmo.angular2.chart.annotation';
import {CompactSummaryTables} from '../../lib/compact-summary-tables';
import {WjChartAnalyticsModule, WjFlexChartWaterfall} from '@mescius/wijmo.angular2.chart.analytics';
import {PortfolioSegmentDataContext} from '../../data/portfolio-segment-data-context.service';
import {Stdlib} from '../../lib/stdlib';
import {
    PfReportDataHelper,
    PfTargetUwySummaryRow,
    UwStrategyBucketNewBusiness,
    UwStrategyBucketTotal
} from '../../data/portfolio-segment-report-data';
import {CollectionView, Globalize} from '@mescius/wijmo';
import {
    ExcelFmtEditable,
    ExcelFmtEditableNotAllowed, ExcelFmtReadonly,
    ExcelRowFormat,
    WjGridCustomCells
} from '../../lib/wjgrid-customcells';
import {
    AtsScatterChartComponent,
    ChartDataPoint,
    ChartTieringRange
} from '../../components/ats-scatter-chart/ats-scatter-chart.component';
import {HitTestInfo} from '@mescius/wijmo.chart';
import {AccessControlService} from '../../security/access-control.service';
import {AppConstants} from '../../app.constants';
import {MathHelper} from '../../lib/math';
import {CellRangeEventArgs, FlexGrid} from '@mescius/wijmo.grid';
import {PortfolioDataContext} from '../../data/portfolio-data-context.service';
import {WjCharts} from '../../lib/wjcharts';

const generateTargetInfoColumns = CompactSummaryTables.generateTargetInfoColumns;
const generateTargetInfoColumnsGnwpNlr = CompactSummaryTables.generateTargetInfoColumnsGnwpNlr;

@Component({
    selector: 'app-dashboard-tracking-main',
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
    standalone: true,
    imports: [
        IntersectionObserverDirective,
        WjGridModule,
        WjChartModule,
        WjChartAnnotationModule, WjChartInteractionModule, WjChartAnalyticsModule, WjNavModule, AtsScatterChartComponent,
    ],
    templateUrl: './dashboard-tracking-main.component.html',
    styleUrl: './dashboard-tracking-main.component.scss'
})
export class DashboardTrackingMainComponent {

    protected readonly Wijmo = Wijmo;

    protected readonly GeneraliWaterfallChartStyle = WjCharts.GeneraliWaterfallChartStyle;

    protected readonly pfData = inject(PortfolioDataContext);
    protected readonly pfSegData = inject(PortfolioSegmentDataContext);

    private readonly gridNewUwStrategy = viewChild<WjFlexGrid>('gridNewUwStrategy');
    private readonly gridAdditionalInfo = viewChild<WjFlexGrid>('gridAdditionalInfo');
    private readonly gridAdditionalInfo2 = viewChild<WjFlexGrid>('gridAdditionalInfo2');
    private readonly gridRateChangeCalibration = viewChild<WjFlexGrid>('gridRateChangeCalibration');

    protected readonly atsChartData = computed(() => this.pfSegData.getAccountDataInFilterPeriodAndTouchFormulaEval() as ChartDataPoint[]);
    protected readonly atsChartRanges = computed(() => (this.pfSegData.classificationTierSchema.value()?.tierTargets ?? []) as ChartTieringRange[])

    protected readonly canSeeRateChangeCalibration = computed(() => {
        return this.accessControlSvc.isSuperuser() || (this.accessControlSvc.isTechEx() && this.accessControlSvc.isCentralTeam());
    });

    protected readonly canApplyRateChangeAdjustment = computed(() => {
        return this.canSeeRateChangeCalibration() && this.accessControlSvc.canEditSegmentCtx(this.pfSegData).allowed;
    });

    protected readonly isRateChangeCalibrationVisible = signal(AppConstants.getApplyRateChangeSectionVisible());

    private readonly premiumWalkChart = viewChild<WjFlexChartWaterfall>('wfc');
    protected WfcCategoryText = { 'font-size': '12px', 'font-weight': 'normal' };

    private readonly accessControlSvc = inject(AccessControlService);

    constructor() {
        effect(async () => await this.prepareReportData());
        effect(() => this.preparePremiumWalkData());
    }

    private editableIfGranted() {
        const canEditSegment = this.accessControlSvc.canEditSegmentCtx(this.pfSegData).allowed;
        return canEditSegment ? ExcelFmtEditable : ExcelFmtEditableNotAllowed;
    }

    private async pfClassificationTiers(): Promise<string[]> {
        await this.pfSegData.waitForResourcesToLoad();
        return this.pfSegData.classificationTierSchema.value()!.tiersNamesHighestFirst().slice();
    }

    // ===========================  Table "New UW Strategy"  ================================
    // A relatively big table with a few editable cells that require immediate update
    // recalculation of formulas. The table has multiple column groups, and some columns
    // are not directly bound to the data source but are calculated based on other columns.
    // =======================================================================================
    async onNewUwStrategyGridInitialized(grid: WjFlexGrid) {
        const newRemainingCols = [
            CompactSummaryTables.rdCol('Rate Change', 'targetRateChangeRemaining', 'p1'),
            CompactSummaryTables.rdCol('Retention Rate', 'targetRetentionRateRemaining', 'p1')
        ];

        const columns: ColumnGroupDeclaration[] = [
            { header: ' ', binding: 'bucket', width: 140, align: 'center', cssClass: 'bold-text' },
            { header: 'Original Portfolio Targets', align: 'center', columns: generateTargetInfoColumns('Org') },
            { header: 'Renewed Portfolio', align: 'center', columns: generateTargetInfoColumns('Renewed') },
            { header: 'Suggested Remaining Target', align: 'center', columns: generateTargetInfoColumns('Remaining') },
            { header: 'New Remaining', align: 'center', columns: newRemainingCols },
            { header: 'New Total Targets', align: 'center', columns: generateTargetInfoColumns('NewTotal') },
        ];

        WjGridUtils.addColumnGroups(grid, columns);
        WjGridUtils.setHeaderRowHeights(grid, 20, 40);
        DashboardTargetsMainComponent.decorateGridWithDashboardDefaultBehaviour(grid);

        WjGridUtils.addCellValueChangeHandler(grid, this.onCellValueChanged.bind(this));
    }

    private onCellValueChanged(s: FlexGrid, e: CellRangeEventArgs){
        const item = e.getRow().dataItem;
        const bucket = item.bucket;
        const binding = e.getColumn().binding!;

        if (bucket === UwStrategyBucketNewBusiness) {
            switch (binding) {
                case 'gnwpEurNewTotal':
                    this.pfSegData.setParameter(AppConstants.PYFORMULA_NEW_BUSINESS_GNWP_EUR_NEW_TOTAL_TARGETS, item.gnwpEurNewTotal);
                    break;
                case 'gnwpEurRenewed':
                    this.pfSegData.setParameter(AppConstants.PYFORMULA_NEW_BUSINESS_GNWP_EUR, item.gnwpEurRenewed);
                    break;
                case 'nlrRenewed':
                    this.pfSegData.setParameter(AppConstants.PYFORMULA_NEW_BUSINESS_NLR, item.nlrRenewed);
                    break;
            }
        } else if (binding.endsWith('Remaining')) {
            this.pfSegData.updateClassificationTierSchemaValue(bucket, binding, item[binding as keyof PfTargetUwySummaryRow]);
        }
        this.pfSegData.updateFormulasAndValidations();
    }

    // ===========================  Table "Additional Info"  ================================
    // A relatively small table with a few editable cells that also require immediate
    // update of the underlying data context and recalculation of formulas.
    // The table has a single column group.
    // =======================================================================================
    async onAdditionalInfoGridInitialized(grid: WjFlexGrid) {
        const columns: ColumnGroupDeclaration[] = [
            { header: ' ', binding: 'bucket', width: 140, align: 'center', cssClass: 'bold-text' },
            { header: 'Renewed Portfolio', align: 'center', columns: generateTargetInfoColumns('RenewedUwy') },
        ];
        WjGridUtils.addColumnGroups(grid, columns);
        WjGridUtils.setHeaderRowHeights(grid, 20, 40);

        DashboardTargetsMainComponent.decorateGridWithDashboardDefaultBehaviour(grid);
        WjGridUtils.addCellValueChangeHandler(grid, this.onAdditionalInfoCellValueChanged.bind(this));
    }

    private onAdditionalInfoCellValueChanged(s: FlexGrid, e: CellRangeEventArgs){
        const item = e.getRow().dataItem;
        const bucket = item.bucket;
        const binding = e.getColumn().binding!;

        if (bucket === UwStrategyBucketNewBusiness) {
            switch (binding) {
                case 'gnwpEurRenewedUwy':
                    this.pfSegData.setParameter(AppConstants.PYFORMULA_NEW_BUSINESS_GNWP_EUR_RENWED_UWY, item.gnwpEurRenewedUwy);
                    break;
            }
            this.pfSegData.updateFormulasAndValidations();
        }

    }

    // ===========================  Table "Additional Info 2"  ================================
    // Small table without any formulas or immediate updates
    // =======================================================================================
    async onAdditionalInfo2GridInitialized(grid: WjFlexGrid) {
        function genCols(gnwpBinding: string, nlrBinding: string) {
            const cols = structuredClone(CompactSummaryTables.TargetInfoColumnsGnwpNlr);
            cols[0].binding = gnwpBinding;
            cols[1].binding = nlrBinding;
            return cols;
        }

        const columns: ColumnGroupDeclaration[] = [
            { header: ' ', binding: 'bucket', width: 140, height: 100, align: 'center', cssClass: 'bold-text' },
            { header: 'Renewed Portfolio (Pre)', align: 'center',
                columns: genCols('expGnwpEurRenewed', 'expNlrRenewed')
            },
            { header: 'Lost/Cancelled Portfolio', align: 'center',
                columns: genCols('expGnwpEurLostCancelled', 'expNlrLostCancelled')

            },
            { header: 'Expiring', align: 'center',
                columns: genCols('expGnwpEurRemaining', 'expNlrRemaining')
            },
        ];

        WjGridUtils.addColumnGroups(grid, columns);
        WjGridUtils.setHeaderRowHeights(grid, 40, 40);
        DashboardTargetsMainComponent.decorateGridWithDashboardDefaultBehaviour(grid);
    }

    // ===========================  Table "Additional Info 2"  ================================
    // Small table without any formulas or immediate updates
    // =======================================================================================
    async onRateChangeCalibrationGridInitialized(grid: WjFlexGrid) {
        WjGridUtils.addColumns(grid, [
            { header: 'Bucket', binding: 'index', isReadOnly: true, isRequired: false },
            { header: 'Rate Change Adj FGU', binding: 'rate_change_adj_fgu', format: 'p2', isReadOnly: true, isRequired: false },
            { header: 'Average Score', binding: 'avg_score', format: 'n2', isReadOnly: true, isRequired: false },
            { header: 'Adjusting factor (calc)', binding: 'adjusting_factor', format: 'p4', isReadOnly: false, isRequired: false },
            { header: 'Adjusting factor (used)', binding: 'targetRateChangeAdjustment', format: 'p4', isReadOnly: false, isRequired: false },
            { header: 'Values to minimize', binding: 'rate_change_delta', format: 'n6', isReadOnly: true, isRequired: false },
        ]);
        WjGridCustomCells.setColumnFormat(grid.getColumn('targetRateChangeAdjustment'), this.editableIfGranted());
        DashboardTargetsMainComponent.decorateGridWithDashboardDefaultBehaviour(grid);
        WjGridUtils.setHeaderRowHeights(grid, 40);
    }

    protected async applyRateChangeCalibration(grid: WjFlexGrid) {
        const trciTable = grid.itemsSource as any[];
        this.pfSegData.classificationTierSchema.value().tierTargets.forEach(tier => {
            const row = trciTable.find(r => r.index === tier.tierName);
            if (row) {
                tier.targetRateChangeAdjustment = row.targetRateChangeAdjustment;
            }
        });
        this.pfSegData.updateFormulasAndValidations()
    }

    /**
     * Prepares and processes the report data to be displayed in all the tables,
     * including updating summary rows, making specific fields editable, and adding
     * calculated fields. The method is designed to handle the specific requirements
     * of different table views such as "New UW Strategy," "Additional Info," and
     * others by updating grid data sources and applying the necessary formatting.
     */
    private async prepareReportData() {
        Stdlib.touchSignals(this.pfSegData.wb.uwySummary);
        await this.pfSegData.waitForResourcesToLoad();
        const summary = structuredClone(this.pfSegData.wb.uwySummary());
        if (summary.length === 0) return;

        // insert a subtotal row before 'New Business'
        const newBusinessIdx = PfReportDataHelper.findSummaryRowIndex(summary, UwStrategyBucketNewBusiness);
        if (newBusinessIdx > 0) {
            const rowFmt: ExcelRowFormat = { cssClass: 'white-on-white', height: 8, isReadOnly: true };
            const separatorRow: any  = { bucket: '', rowFmt };
            summary.splice(newBusinessIdx, 0, separatorRow);
        }

        // make certain fields editable: here a little search function
        // that allows quick lookup of UwStrategy rows by bucket
        const uwStrategyRowByBucket = (bucket: string) => {
            return summary.find(d => d.bucket === bucket)!;
        }

        const tiers = await this.pfClassificationTiers();
        const tierData = (tier: string) => {
            return this.pfSegData.classificationTierSchema.value()!.tierData(tier)!;
        };

        // In the "New UW Stragety" Grid, the 'new remaining' columns for
        // the regular buckets (classic 'PIL') are editable
        tiers.forEach(tier => {
            const row = uwStrategyRowByBucket(tier) as any;
            const tierInfo = tierData(tier);

            row.targetRateChangeRemaining = tierInfo.targetRateChangeRemaining ?? 0;
            row.targetRetentionRateRemaining = tierInfo.targetRetentionRateRemaining ?? 0;
            row.targetRateChangeRemainingCellFmt = this.editableIfGranted();
            row.targetRetentionRateRemainingCellFmt = this.editableIfGranted();
        });

        // Editable are also the 'Renewed Portfolio' columns for 'New Business' category
        const newBusinessRow = uwStrategyRowByBucket(UwStrategyBucketNewBusiness) as any;
        newBusinessRow.gnwpEurRenewedCellFmt = this.editableIfGranted();
        newBusinessRow.nlrRenewedCellFmt = this.editableIfGranted();

        // And the 'New Total Targets' columns for 'New Business' category'
        newBusinessRow.gnwpEurNewTotalCellFmt = this.editableIfGranted();

        // And last - the "Additional Info" table has an editable cell 'New Business' / 'GNWP EUR'
        newBusinessRow.gnwpEurRenewedUwyCellFmt = this.editableIfGranted();

        type PfTargetUwySummaryRowCalculations = {
            [key : string]: (row: PfTargetUwySummaryRow) => number | undefined;
        }

        // generate artificial '...Org' columns for the table 'New UW Strategy'
        // which represent the same values as the '...Renewed' columns, but in local currency
        const reportCalculations: PfTargetUwySummaryRowCalculations = {
            gnwpEurOrg: ($) => $.renewalGnwpEur,
            nlrOrg: ($) => $.renewalNlr,
            rateChangeOrg: ($) => $.targetRateChange,
            retentionRateOrg: ($) => $.targetRetentionRate,
        }

        const calculatedSummary = new CollectionView(summary, { calculatedFields: reportCalculations });
        WjGridUtils.updateGridItemsSourceAndPreserveSelection(this.gridNewUwStrategy(), calculatedSummary);
        WjGridUtils.updateGridItemsSourceAndPreserveSelection(this.gridAdditionalInfo(), calculatedSummary);
        WjGridUtils.updateGridItemsSourceAndPreserveSelection(this.gridAdditionalInfo2(), calculatedSummary);
        WjGridUtils.updateGridItemsSourceAndPreserveSelection(this.gridRateChangeCalibration(), this.pfSegData.wb.rateChangeInterpolationTable());
    }

    /**
     * Prepares and processes data needed for the premium walk chart visualization.
     * This method calculates various premium-related metrics such as expiring premium,
     * premium changes due to lost accounts, exposure variation, effective rate changes (ERC),
     * and new business, and organizes them for chart rendering.
     *
     * The resulting data points are grouped and formatted to provide meaningful insights,
     * including intermediate totals and labels for the premium walk chart. The method also
     * sets up chart configurations such as tooltip content and total labels.
     */
    private async preparePremiumWalkData() {
        const summary = structuredClone(this.pfSegData.wb.uwySummary());
        const chart = this.premiumWalkChart();
        const totalRow = summary.find(row => row.bucket === UwStrategyBucketTotal);
        const renewedAccounts = this.pfSegData.getAccountData().filter(acc => acc.renewalStatus === 'Renewed');

        if (totalRow && chart) {
            const nv = MathHelper.zeroIfNull;
            function ndiv(dividend: number, divisor: number): number {
                if (divisor === 0) return 0;
                else return dividend / divisor;
            }

            const expiredPremiumYtd = nv(totalRow.expGnwpEurRenewed) + nv(totalRow.expGnwpEurLostCancelled);
            const premDecrDueToLost = -nv(totalRow.expGnwpEurLostCancelled);

            const totalRetainedGnwpRenewed = sumBy(renewedAccounts, a => nv(a.retainedGnwp))
            const totalGnwpRenewed = sumBy(renewedAccounts, a => nv(a.gnwpEur))

            const premDueToExposureVarRatio = ndiv(totalRetainedGnwpRenewed, totalGnwpRenewed);
            const premDueToExposureVar = (premDueToExposureVarRatio - 1.0) * (premDecrDueToLost + expiredPremiumYtd);

            const totalRetainedGnwpByErcRenewed = sumBy(renewedAccounts, a => nv(a.effectiveRateChange) * nv(a.retainedGnwp));
            const premDueToErcRatio = ndiv(totalRetainedGnwpByErcRenewed, totalRetainedGnwpRenewed);
            const premDueToErc = premDueToErcRatio * nv(expiredPremiumYtd + premDecrDueToLost);
            const newBusinessYtd = nv(this.pfSegData.getParameter(AppConstants.PYFORMULA_NEW_BUSINESS_GNWP_EUR) as number);

            const estimatedPremiumAtYe = nv(totalRow.gnwpEurNewTotal);
            const premRemaining = estimatedPremiumAtYe - expiredPremiumYtd - premDecrDueToLost - premDueToExposureVar - premDueToErc  - newBusinessYtd;

            const chartData = [
                { name: "Expiring Premium YTD", value: expiredPremiumYtd, label: '' },
                { name: "Premium decrease due to lost Accounts", value: premDecrDueToLost },
                { name: "Premium change due to Exposure variation", value: premDueToExposureVar },
                { name: "Premium change due to ERC", value: premDueToErc, isFollowedByTotal: 'Renewed Premium' },
                { name: "New Business YTD", value: newBusinessYtd, isFollowedByTotal: "Total Renewed Premium" },
                { name: "Remaining Target Premium inc New Business", value: premRemaining, isFollowedByTotal: "Estimated Premium at YE" },
            ];
            chartData.forEach(it => it.label = Globalize.format(it.value, 'n1,,'));

            const totals = chartData
                .map((item, index) => ({ item, index }))
                .filter(it => it.item.isFollowedByTotal);

            const totalPositions = totals.map(it => it.index + 1);
            const totalLabels = totals.map(it => it.item.isFollowedByTotal);

            setTimeout(() => {
                chart.tooltipContent = (hti: HitTestInfo) => `${hti.x}: ${Globalize.format(hti.y, 'n1,,')}`
                chart.chart.itemsSource = chartData;
                chart.intermediateTotalPositions = totalPositions;
                chart.intermediateTotalLabels = totalLabels;
                chart.showIntermediateTotal = true;
                chart.totalLabel = '**Total';
            }, 0);
        }
    }

    protected toggleRateChangeVisibility(gridRateChangeCalibration: WjFlexGrid) {
        const newValue = !this.isRateChangeCalibrationVisible();
        AppConstants.setApplyRateChangeSectionVisible(newValue);
        this.isRateChangeCalibrationVisible.set(newValue);
    }
}
